import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { PreviewCanvasModelInstance } from '../../../store/_models/previewCanvas';
import CanvasBorders from '@photosi/rn-components/product-preview/canvas-preview/canvas-borders/CanvasBorders';
import { DATADOG_CUSTOM_ACTIONS } from '../../../_shared/datadog/DatadogCustomActions';
import Datadog from '../../../_shared/datadog/index.web';
import { useResponsiveScale } from '../../../contexts/ResponsiveScaleContext';
import { observer } from 'mobx-react-lite';
import { CanvasBorderType } from '@photosi/expo-photosi-sdk/build/ImageEditor';
import { PreviewContent } from '@photosi/rn-components/preview-content/preview-content/PreviewContent';
import { productPreviewTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import Animated, {
  SharedValue,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { brand } from 'src/theme/colors';

type CanvasPreviewProps = {
  preview: PreviewCanvasModelInstance;
  editable: boolean;
  showGrid: boolean;
  disabled?: boolean;
  disableSwapImageAnimation?: boolean;
  isHighlighted?: SharedValue<boolean> | boolean;
  showBadges?: boolean;
};

const CanvasPreview = observer(
  ({
    preview,
    disabled,
    editable,
    showGrid,
    disableSwapImageAnimation = false,
    isHighlighted = false,
    showBadges = true,
  }: CanvasPreviewProps) => {
    const { responsiveScaleSV, responsiveScale } = useResponsiveScale();

    const borderAnimatedStyle = useAnimatedStyle(() => {
      if (typeof isHighlighted === 'object' && isHighlighted?.value !== undefined) {
        // SharedValue case - animated
        return {
          opacity: isHighlighted.value ? 1 : 0,
        };
      } else if (typeof isHighlighted === 'boolean') {
        // Boolean case - static
        return {
          opacity: isHighlighted ? 1 : 0,
        };
      }
      return {
        opacity: 0,
      };
    }, [isHighlighted]);
    const transformScaleSV = useSharedValue(1);
    useDerivedValue(() => {
      if (typeof isHighlighted === 'object' && isHighlighted?.value !== undefined) {
        // SharedValue case - animated
        transformScaleSV.value = withTiming(isHighlighted.value ? 0.98 : 1, { duration: 250 });
      } else if (typeof isHighlighted === 'boolean') {
        // Boolean case - static
        transformScaleSV.value = withTiming(isHighlighted ? 0.98 : 1, { duration: 250 });
      }
    }, [isHighlighted]);

    useEffect(() => {
      Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.configuration_option_apply_time);
    }, [preview?.borderWidth]);

    const sizeAnimatedStyle = useAnimatedStyle(
      () => ({
        width: preview.viewBox.width * responsiveScaleSV.value,
        height: preview.viewBox.height * responsiveScaleSV.value,
        transform: [{ scale: transformScaleSV.value }],
      }),
      [responsiveScaleSV, preview.viewBox.width, preview.viewBox.height, transformScaleSV, responsiveScale],
    );

    const userImage = preview.userImageArea.userImage;
    const qualityBadgePadding =
      userImage?.borderType === CanvasBorderType.FOLDED ? preview.borderWidth * responsiveScale : 0;
    return (
      // PREVIEW CONTAINER
      <View
        style={[styles.previewContainer]}
        testID={productPreviewTestID(preview.userImageArea.productId, TestIDs.CANVAS)}
      >
        <Animated.View style={[sizeAnimatedStyle]}>
          {/* BORDERS */}
          <CanvasBorders preview={preview} />
          {/*USER IMAGES */}
          <PreviewContent
            disabled={disabled}
            editable={editable}
            showGrid={showGrid}
            preview={preview}
            disableSwapImageAnimation={disableSwapImageAnimation}
            qualityBadgePadding={qualityBadgePadding}
            showBadges={showBadges}
          />
        </Animated.View>
        {/* HIGHLIGHTED BORDER */}
        <Animated.View style={[styles.highlighted, sizeAnimatedStyle, borderAnimatedStyle]} />
      </View>
    );
  },
);

const styles = StyleSheet.create({
  previewContainer: {
    width: '100%',
    maxHeight: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  highlighted: {
    position: 'absolute',
    borderColor: brand.main.water,
    borderWidth: 2,
    borderRadius: 4,
    pointerEvents: 'none',
  },
});

export default CanvasPreview;
